'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, Zap, ChevronDown } from 'lucide-react';
import type { PaperPortfolio, PaperPosition } from '../utils/paperTradingStorage';
import { savePortfolio } from '../utils/paperTradingStorage';

const SYMBOLS = [
  { value: 'NVDA', label: 'NVDA – Nvidia', basePrice: 875.4 },
  { value: 'AAPL', label: 'AAPL – Apple', basePrice: 213.5 },
  { value: 'TSLA', label: 'TSLA – Tesla', basePrice: 248.7 },
  { value: 'SPY', label: 'SPY – S&P 500 ETF', basePrice: 542.3 },
  { value: 'QQQ', label: 'QQQ – Nasdaq ETF', basePrice: 468.9 },
  { value: 'META', label: 'META – Meta', basePrice: 512.6 },
  { value: 'MSFT', label: 'MSFT – Microsoft', basePrice: 415.2 },
  { value: 'AMZN', label: 'AMZN – Amazon', basePrice: 186.4 },
  { value: 'GC=F', label: 'GC – Gold', basePrice: 2345.0 },
  { value: 'CL=F', label: 'CL – Crude Oil', basePrice: 78.5 },
  { value: 'ES=F', label: 'ES – S&P 500 Futures', basePrice: 5420.0 },
  { value: 'NQ=F', label: 'NQ – Nasdaq Futures', basePrice: 18950.0 },
];

const STRATEGIES = [
  'Breakout above resistance',
  'VWAP reclaim + momentum',
  'Gap fill + reversal',
  'Trend following',
  'Mean reversion',
];

// Simulate live price with small random walk
function simulatePrice(base: number, seed: number): number {
  const noise = (Math.sin(seed * 0.1) * 0.003 + Math.cos(seed * 0.07) * 0.002) * base;
  return Math.max(base * 0.5, base + noise);
}

interface TradeExecutionPanelProps {
  portfolio: PaperPortfolio;
  onPortfolioChange: (p: PaperPortfolio) => void;
  priceTick: number;
}

export default function TradeExecutionPanel({
  portfolio,
  onPortfolioChange,
  priceTick,
}: TradeExecutionPanelProps) {
  const [symbol, setSymbol] = useState(SYMBOLS[0].value);
  const [direction, setDirection] = useState<'long' | 'short'>('long');
  const [quantity, setQuantity] = useState('10');
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [strategy, setStrategy] = useState(STRATEGIES[0]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const selectedSymbol = SYMBOLS.find((s) => s.value === symbol) || SYMBOLS[0];
  const livePrice = simulatePrice(selectedSymbol.basePrice, priceTick);
  const qty = parseFloat(quantity) || 0;
  const orderValue = livePrice * qty;
  const canAfford = orderValue <= portfolio.virtualCash;

  const handleExecute = useCallback(() => {
    setError('');
    setSuccess('');

    const q = parseFloat(quantity);
    if (!q || q <= 0) { setError('Enter a valid quantity.'); return; }
    if (orderValue > portfolio.virtualCash) {
      setError(`Insufficient cash. Need $${orderValue.toFixed(2)}, have $${portfolio.virtualCash.toFixed(2)}.`);
      return;
    }

    const newPosition: PaperPosition = {
      id: `pos-${Date.now()}`,
      symbol,
      direction,
      entryPrice: livePrice,
      quantity: q,
      stopLoss: stopLoss ? parseFloat(stopLoss) : null,
      takeProfit: takeProfit ? parseFloat(takeProfit) : null,
      openedAt: new Date().toISOString(),
      strategy,
      currentPrice: livePrice,
    };

    const updated: PaperPortfolio = {
      ...portfolio,
      virtualCash: portfolio.virtualCash - orderValue,
      positions: [...portfolio.positions, newPosition],
    };

    savePortfolio(updated);
    onPortfolioChange(updated);
    setSuccess(`✓ ${direction === 'long' ? 'Bought' : 'Shorted'} ${q} × ${symbol} @ $${livePrice.toFixed(2)}`);
    setQuantity('10');
    setStopLoss('');
    setTakeProfit('');
    setTimeout(() => setSuccess(''), 4000);
  }, [quantity, orderValue, portfolio, symbol, direction, livePrice, stopLoss, takeProfit, strategy, onPortfolioChange]);

  return (
    <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-700 text-foreground">Execute Trade</h2>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-xs text-emerald-400 font-500">Live Sim</span>
        </div>
      </div>

      {/* Symbol */}
      <div className="space-y-1.5">
        <label className="text-xs font-500 text-muted-foreground">Symbol</label>
        <div className="relative">
          <select
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            className="w-full appearance-none bg-background border border-border rounded-xl px-3 py-2.5 text-sm text-foreground pr-8 focus:outline-none focus:border-primary/50"
          >
            {SYMBOLS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        </div>
      </div>

      {/* Live price display */}
      <div className="bg-background rounded-xl px-4 py-3 flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Simulated Price</span>
        <div className="text-right">
          <span className="text-lg font-700 text-foreground tabular-nums">
            ${livePrice.toFixed(2)}
          </span>
          <span className="text-xs text-muted-foreground ml-1">{symbol}</span>
        </div>
      </div>

      {/* Direction */}
      <div className="space-y-1.5">
        <label className="text-xs font-500 text-muted-foreground">Direction</label>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setDirection('long')}
            className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-600 border transition-all duration-150 ${
              direction === 'long' ?'bg-emerald-500/10 border-emerald-500/40 text-emerald-400' :'bg-background border-border text-muted-foreground hover:border-emerald-500/30'
            }`}
          >
            <TrendingUp size={15} /> Long
          </button>
          <button
            onClick={() => setDirection('short')}
            className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-600 border transition-all duration-150 ${
              direction === 'short' ?'bg-red-500/10 border-red-500/40 text-red-400' :'bg-background border-border text-muted-foreground hover:border-red-500/30'
            }`}
          >
            <TrendingDown size={15} /> Short
          </button>
        </div>
      </div>

      {/* Quantity */}
      <div className="space-y-1.5">
        <label className="text-xs font-500 text-muted-foreground">Quantity (shares)</label>
        <input
          type="number"
          min="1"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          className="w-full bg-background border border-border rounded-xl px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50"
          placeholder="10"
        />
        <p className="text-xs text-muted-foreground">
          Order value:{' '}
          <span className={`font-600 ${canAfford ? 'text-foreground' : 'text-red-400'}`}>
            ${orderValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
          {' '}/ Cash:{' '}
          <span className="font-600 text-foreground">
            ${portfolio.virtualCash.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </p>
      </div>

      {/* SL / TP */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-xs font-500 text-muted-foreground">Stop Loss</label>
          <input
            type="number"
            value={stopLoss}
            onChange={(e) => setStopLoss(e.target.value)}
            className="w-full bg-background border border-border rounded-xl px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50"
            placeholder="Optional"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-500 text-muted-foreground">Take Profit</label>
          <input
            type="number"
            value={takeProfit}
            onChange={(e) => setTakeProfit(e.target.value)}
            className="w-full bg-background border border-border rounded-xl px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50"
            placeholder="Optional"
          />
        </div>
      </div>

      {/* Strategy */}
      <div className="space-y-1.5">
        <label className="text-xs font-500 text-muted-foreground">Strategy Tag</label>
        <div className="relative">
          <select
            value={strategy}
            onChange={(e) => setStrategy(e.target.value)}
            className="w-full appearance-none bg-background border border-border rounded-xl px-3 py-2.5 text-sm text-foreground pr-8 focus:outline-none focus:border-primary/50"
          >
            {STRATEGIES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        </div>
      </div>

      {/* Feedback */}
      {error && (
        <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">{error}</p>
      )}
      {success && (
        <p className="text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-3 py-2">{success}</p>
      )}

      {/* Execute button */}
      <button
        onClick={handleExecute}
        disabled={!canAfford || qty <= 0}
        className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-700 transition-all duration-150 ${
          direction === 'long' ?'bg-emerald-500 hover:bg-emerald-400 text-white disabled:opacity-40 disabled:cursor-not-allowed' :'bg-red-500 hover:bg-red-400 text-white disabled:opacity-40 disabled:cursor-not-allowed'
        }`}
      >
        <Zap size={15} />
        {direction === 'long' ? 'Buy / Long' : 'Sell / Short'} {symbol}
      </button>
    </div>
  );
}
